import React, { useEffect, useState } from 'react';
import { DocumentTextIcon, CubeIcon, TagIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import knowledgeService, { KnowledgeStats as Stats } from '../../services/knowledgeService';

interface KnowledgeStatsProps {
  documentCount: number;
  refreshTrigger?: number;
}

const KnowledgeStats: React.FC<KnowledgeStatsProps> = ({ documentCount, refreshTrigger }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [refreshTrigger]);

  const fetchStats = async () => {
    try {
      const data = await knowledgeService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Documents',
      value: documentCount,
      icon: DocumentTextIcon,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      description: 'Total uploaded documents'
    },
    {
      title: 'Document Chunks',
      value: stats?.totalChunks || 0,
      icon: CubeIcon,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      description: 'Total searchable chunks'
    },
    {
      title: 'With Embeddings',
      value: stats?.chunksWithEmbeddings || 0,
      icon: ChartBarIcon,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      description: 'Chunks ready for semantic search',
      percentage: stats?.totalChunks ? 
        Math.round((stats.chunksWithEmbeddings / stats.totalChunks) * 100) : 0
    },
    {
      title: 'Categories',
      value: stats?.categories?.length || 0,
      icon: TagIcon,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      description: 'Unique document categories'
    }
  ];

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.title} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 text-${stat.color.replace('bg-', '')}`} />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  {stat.percentage !== undefined && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({stat.percentage}%)
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Stats */}
      {stats && stats.averageChunkSize > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Average Chunk Size</h4>
              <p className="text-xs text-gray-500">Tokens per chunk for optimal search</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-gray-900">
                {stats.averageChunkSize} tokens
              </p>
              <p className="text-xs text-gray-500">
                {stats.averageChunkSize < 300 ? 'Small chunks' : 
                 stats.averageChunkSize > 700 ? 'Large chunks' : 'Optimal size'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Categories List */}
      {stats && stats.categories && stats.categories.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow-md p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Active Categories</h4>
          <div className="flex flex-wrap gap-2">
            {stats.categories.map((category) => (
              <span
                key={category}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeStats;